// Pure rules for the proposal's document layer (flows/proposta.md R8, R12).
// The numbers live in budget_items; this decides what the client reads/signs
// and which PDF any "download" button must open — never the platform default
// when the operator authored or uploaded a document.

export type ProposalDocumentMode = "auto" | "richtext" | "pdf_upload";

export interface ProposalDocumentInput {
  document_mode: ProposalDocumentMode;
  richtext_content: string | null;
  source_pdf_url: string | null;
  base_pdf_url?: string | null;
  signed_pdf_url?: string | null;
}

/** True when the document can be frozen and sent — the send buttons gate on this. */
export function isProposalDocumentReady(budget: ProposalDocumentInput): boolean {
  if (budget.document_mode === "richtext") return !!budget.richtext_content?.trim();
  if (budget.document_mode === "pdf_upload") return !!budget.source_pdf_url;
  return true;
}

export type ProposalPdfKind = "signed" | "frozen" | "source" | "generated" | "pending";

export interface ProposalPdf {
  kind: ProposalPdfKind;
  /** Where to open the PDF; null for `generated` (build client-side) and `pending` (nothing yet). */
  url: string | null;
  label: string;
  /** Why there is no PDF yet (`pending` only) — shown as the button's tooltip. */
  hint?: string;
}

/**
 * Single source of truth for "which PDF is the proposal": signed > frozen at
 * send time > uploaded file > generated from items (only in `auto`). An
 * authored/uploaded document that is not ready has no PDF until it is sent.
 */
export function resolveProposalPdf(budget: ProposalDocumentInput): ProposalPdf {
  if (budget.signed_pdf_url) {
    return { kind: "signed", url: budget.signed_pdf_url, label: "Baixar PDF assinado" };
  }
  if (budget.base_pdf_url) {
    return { kind: "frozen", url: budget.base_pdf_url, label: "Baixar PDF enviado" };
  }
  if (budget.document_mode === "pdf_upload" && budget.source_pdf_url) {
    return { kind: "source", url: budget.source_pdf_url, label: "Abrir PDF" };
  }
  if (budget.document_mode === "auto") {
    return { kind: "generated", url: null, label: "Pré-visualizar PDF" };
  }
  return {
    kind: "pending",
    url: null,
    label: "PDF",
    hint:
      budget.document_mode === "richtext"
        ? "O PDF é gerado a partir do texto ao enviar para assinatura"
        : "Envie o PDF da proposta para poder abri-lo",
  };
}
